import { useState, useEffect, useCallback } from 'react';
import { getSystemStatus, getSitesStatus } from '../api/client';
import type { SystemStatus, SitesStatus } from '../types';

interface UseStatusResult {
  status: SystemStatus | null;
  sitesStatus: SitesStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useStatus(refreshInterval = 30000, enabled = true): UseStatusResult {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [sitesStatus, setSitesStatus] = useState<SitesStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const [systemStatus, sites] = await Promise.all([
        getSystemStatus(),
        getSitesStatus(),
      ]);
      setStatus(systemStatus);
      setSitesStatus(sites);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch status');
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    // (Re)enabling — e.g. auth just finished restoring — must show loading until
    // the first fetch resolves, otherwise consumers briefly see loading=false
    // with sitesStatus still null (which flashed the site-list/onboarding before
    // the single-site redirect). Interval refreshes call fetchData directly and
    // stay silent (no loading flip), so the dashboard doesn't blink every cycle.
    setLoading(true);
    fetchData();

    if (refreshInterval > 0) {
      const interval = setInterval(fetchData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchData, refreshInterval, enabled]);

  return {
    status,
    sitesStatus,
    loading,
    error,
    refresh: fetchData,
  };
}
